import { Container } from '@components/ui/Container';
import { Section } from '@components/ui/Section';
import { Button } from '@components/ui/Button';
import { ButtonAnchor } from '@components/ui/Button/ButtonAnchor';
import { EventCard } from '@components/ui/EventCard';
import { StatCounter } from '@components/ui/StatCounter';
import { ScrollReveal } from '@components/motion/ScrollReveal';
import { StaggerContainer, StaggerItem } from '@components/motion/StaggerContainer';
import { Parallax } from '@components/motion/Parallax';
import { FadeIn } from '@components/motion/FadeIn';
import { Eyebrow, H2, Lead, Body, GradientText, SectionHeader } from '@components/ui/Typography';
import { getFeaturedEvents } from '@data/events';
import { ROUTES } from '@constants/routes';
import { COMPANY, whatsappUrl } from '@constants/company';
import {
  AboutGrid,
  AboutVisual,
  AboutVisualBg,
  AboutVisualCenter,
  AboutVisualPattern,
  BigLabel,
  BigNumber,
  CtaButtons,
  CtaContent,
  CtaGlow,
  CtaSection,
  EventsGrid,
  EventsSeeAll,
  EyebrowLine,
  EyebrowText,
  HeroBackground,
  HeroBlob,
  HeroContent,
  HeroCtas,
  HeroEyebrow,
  HeroGradient,
  HeroGrid,
  HeroHeading,
  HeroScroll,
  HeroSection,
  HeroSub,
  NewsGrid,
  ScrollDot,
  ScrollLabel,
  StatDivider,
  StatsGrid,
} from './styles';
import { memo } from 'react';
import { news } from '@/data/news';
import { LogoCarousel, NewsCard } from '@/components';
import { clients } from '@/data/clients';
// import { PageTransition } from '@/components';

const featuredEvents = getFeaturedEvents(4);

const Home: React.FC = () => {
  const whatsappMsg = 'Olá! Tenho interesse em ser expositor em um dos eventos da Teia de Eventos.';

  return (
    <>
      <title>Teia de Eventos — Há mais de 26 anos criando experiências</title>
      <meta
        name="description"
        content="Empresa especializada em planejamento, organização e execução de eventos corporativos, culturais e turísticos em Nova Friburgo/RJ."
      />

      {/* HERO */}
      <HeroSection>
        <HeroBackground>
          <HeroGrid />
          <HeroBlob $x="10%" $y="20%" $size="600px" />
          <HeroBlob $x="60%" $y="50%" $size="400px" $delay="3s" />
          <HeroBlob $x="80%" $y="10%" $size="300px" $delay="6s" />
          <HeroGradient />
        </HeroBackground>

        <HeroContent>
          <HeroEyebrow
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } } }}
          >
            <EyebrowLine
              variants={{
                hidden: { width: 0 },
                visible: { width: 32, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
              }}
            />
            <EyebrowText
              variants={{
                hidden: { opacity: 0, x: -8 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              Nova Friburgo, RJ · Desde 2000
            </EyebrowText>
          </HeroEyebrow>

          <HeroHeading
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
          >
            Eventos que <GradientText>ficam na memória.</GradientText>
          </HeroHeading>

          <HeroSub
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
          >
            Há mais de 26 anos planejando, organizando e executando os eventos mais marcantes da
            Região Serrana do Rio de Janeiro — da FEPRO à Festa do Morango com Chocolate.
          </HeroSub>

          <HeroCtas
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
          >
            <Button
              variant="primary"
              size="lg"
              onClick={() => (window.location.href = ROUTES.events)}
            >
              Nossos Eventos
            </Button>
            <ButtonAnchor
              href={whatsappUrl(whatsappMsg)}
              target="_blank"
              rel="noopener noreferrer"
              $variant="outline"
              $size="lg"
              style={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.3)' }}
              whileHover={{ borderColor: 'rgba(255,255,255,0.8)', color: '#fff' }}
            >
              Seja Expositor
            </ButtonAnchor>
          </HeroCtas>
        </HeroContent>

        <HeroScroll
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.6 }}
        >
          <ScrollDot
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <ScrollLabel>scroll</ScrollLabel>
        </HeroScroll>
      </HeroSection>

      {/* SOBRE + NÚMEROS */}
      <Section $bg="inverse" $spacing="md">
        <Container>
          <AboutGrid>
            <ScrollReveal direction="left" distance={60}>
              <AboutVisual>
                <Parallax speed={0.15}>
                  <AboutVisualBg />
                </Parallax>
                <AboutVisualPattern />
                <AboutVisualCenter>
                  <BigNumber>26</BigNumber>
                  <BigLabel>anos de história</BigLabel>
                </AboutVisualCenter>
              </AboutVisual>
            </ScrollReveal>

            <ScrollReveal direction="right" distance={60}>
              <SectionHeader>
                <StatDivider />
                <Eyebrow>Quem somos</Eyebrow>
                <H2 $inverse>
                  Mais de duas décadas <GradientText>conectando pessoas</GradientText>
                </H2>
                <Lead $inverse>
                  A Teia de Eventos nasceu em Nova Friburgo com um propósito claro: transformar
                  ideias em experiências reais que movem pessoas, geram negócios e celebram a
                  cultura da Região Serrana.
                </Lead>
                <Body $inverse>
                  Da concepção à execução, cuidamos de cada detalhe para que expositores,
                  patrocinadores e visitantes vivam algo que vale a pena lembrar.
                </Body>
                <div style={{ marginTop: '1.5rem' }}>
                  <Button
                    variant="outline"
                    inverse
                    onClick={() => (window.location.href = ROUTES.about)}
                  >
                    Conheça nossa história
                  </Button>
                </div>
              </SectionHeader>
            </ScrollReveal>
          </AboutGrid>

          <StatsGrid>
            <StaggerContainer staggerDelay={0.12} initialDelay={0.2}>
              {[
                { value: 26, prefix: '+', label: 'Anos de mercado' },
                { value: 15, prefix: '+', label: 'Eventos anuais' },
                { value: 100, prefix: '+', suffix: 'mil', label: 'Visitantes/ano' },
                { value: 500, prefix: '+', label: 'Expositores atendidos' },
              ].map((stat) => (
                <StaggerItem key={stat.label} direction="up">
                  <StatCounter
                    value={stat.value}
                    suffix={stat.suffix}
                    label={stat.label}
                    prefix={stat.prefix}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </StatsGrid>
        </Container>
      </Section>

      {/* EVENTOS EM DESTAQUE */}
      <Section $bg="secondary" $spacing="md">
        <Container>
          <ScrollReveal>
            <SectionHeader>
              <Eyebrow>Nossos eventos</Eyebrow>
              <H2>
                Uma agenda que <GradientText>move a cidade</GradientText>
              </H2>
              <Lead>
                Da maior feira de promoção da Região Serrana às festas que celebram nossa cultura e
                gastronomia.
              </Lead>
            </SectionHeader>
          </ScrollReveal>

          <EventsGrid>
            <StaggerContainer staggerDelay={0.08} initialDelay={0.1}>
              {featuredEvents.map((event) => (
                <StaggerItem key={event.slug}>
                  <EventCard event={event} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </EventsGrid>

          <EventsSeeAll>
            <ScrollReveal>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => (window.location.href = ROUTES.events)}
              >
                Ver todos os eventos
              </Button>
            </ScrollReveal>
          </EventsSeeAll>
        </Container>
      </Section>

      {/* NEWS */}
      <Section $bg="secondary" $spacing="md">
        <Container>
          <ScrollReveal>
            <SectionHeader>
              <Eyebrow>News</Eyebrow>
              <H2>
                Últimas <GradientText>notícias</GradientText>
              </H2>
              <Lead>Fique por dentro de tudo que acontece nos nossos eventos.</Lead>
            </SectionHeader>
          </ScrollReveal>

          <NewsGrid>
            {news.map((article, i) => (
              <div key={article.id}>
                <ScrollReveal direction="up" delay={i * 0.1} threshold={0.1}>
                  <NewsCard
                    article={article}
                    onClick={() =>
                      article.eventSlug && (window.location.href = `/eventos/${article.eventSlug}`)
                    }
                  />
                </ScrollReveal>
              </div>
            ))}
          </NewsGrid>
        </Container>
      </Section>

      {/* CLIENTES */}
      {clients.length > 0 && (
        <Section $bg="primary" $spacing="sm">
          <Container>
            <ScrollReveal direction="up">
              <LogoCarousel clients={clients} />
            </ScrollReveal>
          </Container>
        </Section>
      )}
      {/* CTA FINAL */}
      <CtaSection>
        <CtaGlow />
        <CtaContent>
          <FadeIn direction="up">
            <Eyebrow style={{ justifyContent: 'center' }}>Vamos conversar</Eyebrow>
          </FadeIn>
          <FadeIn direction="up" delay={0.1}>
            <H2 $inverse style={{ textAlign: 'center', maxWidth: '18ch', margin: '0 auto' }}>
              Seu próximo evento começa aqui.
            </H2>
          </FadeIn>
          <FadeIn direction="up" delay={0.2}>
            <Lead $inverse style={{ textAlign: 'center', margin: '0 auto' }}>
              Seja para expor sua marca, patrocinar um evento ou organizar uma celebração — estamos
              prontos para transformar sua ideia em realidade.
            </Lead>
          </FadeIn>
          <FadeIn direction="up" delay={0.3}>
            <CtaButtons>
              <ButtonAnchor
                href={whatsappUrl(whatsappMsg)}
                target="_blank"
                rel="noopener noreferrer"
                $variant="primary"
                $size="lg"
                whileTap={{ scale: 0.96 }}
              >
                Falar pelo WhatsApp
              </ButtonAnchor>
              <ButtonAnchor
                href={`mailto:${COMPANY.contact.email}`}
                $variant="ghost"
                $size="lg"
                style={{ color: 'rgba(255,255,255,0.75)' }}
              >
                Enviar e-mail
              </ButtonAnchor>
            </CtaButtons>
          </FadeIn>
          <FadeIn direction="none" delay={0.5}>
            <Body $size="sm" $inverse style={{ opacity: 0.4, textAlign: 'center' }}>
              {COMPANY.hours} · {COMPANY.address.city}, {COMPANY.address.state}
            </Body>
          </FadeIn>
        </CtaContent>
      </CtaSection>
    </>
  );
};

export default memo(Home);
